import { useState } from "react";

import { CheckOperation } from "@workspace/operations/check-operation";
import type { Operation } from "@workspace/operations/types";
import { Button } from "@workspace/ui/components/button";

import type { Order } from "../../types";
import { isCompletedByComps, wholeOrderComp } from "../comp";
import { canChangeOrderMoney } from "../money-change";
import { CompDialog } from "./comp-dialog";

/**
 * HA-658 slice 4 — the whole-order Comp (mark free) control, and Remove Comp once the whole order is comped. Gated on the comp
 * operation (the same key the routes enforce: hiding is the courtesy, the 403 is the rule), and only offered while the order can
 * take a money change; an order the comps Completed keeps Remove Comp so the comp can be undone (35-15). Server-confirmed: the
 * handlers resolve once the server has answered and reject with its refusal.
 */
export function OrderCompControl({
	order,
	operations,
	onComp,
	onRemove,
}: {
	order: Pick<
		Order,
		| "status"
		| "items"
		| "payments"
		| "comps"
		| "isFrozen"
		| "activeSplitGroup"
		| "fullyComped"
		| "completedByComps"
	>;
	operations: Operation[];
	onComp: (reason: string) => void | Promise<unknown>;
	onRemove: () => void | Promise<unknown>;
}) {
	const [prompting, setPrompting] = useState(false);
	const [removing, setRemoving] = useState(false);

	const wholeComp = wholeOrderComp(order);
	const canChange = canChangeOrderMoney(order);
	const undoable = canChange || isCompletedByComps(order);

	const remove = async () => {
		setRemoving(true);
		try {
			await onRemove();
		} catch {
			// The caller has told the operator what the server said.
		} finally {
			setRemoving(false);
		}
	};

	return (
		<CheckOperation operation="staff-terminal-comp" operations={operations}>
			{wholeComp
				? undoable && (
						<Button
							variant="secondary"
							size="lg"
							className="mt-4 h-16 grow"
							disabled={removing}
							onClick={() => void remove()}
						>
							Remove Comp
						</Button>
					)
				: canChange &&
					order.items.length > 0 && (
						<>
							<Button
								variant="secondary"
								size="lg"
								className="mt-4 h-16 grow"
								onClick={() => setPrompting(true)}
							>
								Comp (mark free)
							</Button>
							<CompDialog
								open={prompting}
								title="Comp this order"
								description="Mark every line free. They are still made, and their stock still deducts."
								onConfirm={onComp}
								onClose={() => setPrompting(false)}
							/>
						</>
					)}
		</CheckOperation>
	);
}
